import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Search, Globe, Loader2, TrendingDown, TrendingUp, RefreshCw, History } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = ['', 'kirana', 'vegetables', 'dairy', 'bakery', 'fancy', 'cloth', 'hotels', 'medical',
  'electronics', 'electrical', 'mobiles', 'books', 'artifacts', 'jewellery'];

export default function AdminPriceWatch() {
  const [form, setForm] = useState({ product_name: '', category: '', unit: '' });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recent, setRecent] = useState([]);

  const loadRecent = async () => {
    try {
      const r = await api.get('/owner/price-lookup/recent', { params: { limit: 30 } });
      setRecent(r.data || []);
    } catch (e) { console.warn('[AdminPriceWatch] recent load failed:', e?.message); }
  };
  useEffect(() => { loadRecent(); }, []);

  const lookup = async (overrides = {}) => {
    const payload = { ...form, ...overrides };
    if (!payload.product_name || payload.product_name.trim().length < 2) {
      toast.error('Enter at least 2 characters for the product name'); return;
    }
    setLoading(true);
    try {
      const r = await api.post('/owner/price-lookup', payload);
      setResult(r.data);
      loadRecent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not fetch price');
    } finally { setLoading(false); }
  };

  return (
    <section className="mt-6">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-12 h-12 rounded-xl bg-[#0D47A1] text-white grid place-items-center"><Globe className="w-6 h-6"/></div>
        <div className="flex-1 min-w-[200px]">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#0D47A1]">Price intelligence</div>
          <h2 className="font-heading font-bold text-2xl">Live market Price Watch</h2>
          <p className="text-xs text-ink/60 mt-1">
            Look up the latest Indian retail average and the lowest online price for any item.
            Visible to platform Admin and shop Owners — not to customers. Results cached for 6&nbsp;hours.
          </p>
        </div>
      </div>

      <div className="card-soft p-4 mt-5 grid md:grid-cols-12 gap-2 items-end">
        <div className="md:col-span-5">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink/60 mb-1">Product</div>
          <input
            data-testid="price-watch-product"
            value={form.product_name}
            onChange={e => setForm({ ...form, product_name: e.target.value })}
            onKeyDown={e => { if (e.key === 'Enter') lookup(); }}
            placeholder="e.g. Aashirvaad Atta 5kg"
            className="w-full border border-border bg-white rounded-lg px-3 py-2.5 text-sm"
          />
        </div>
        <div className="md:col-span-3">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink/60 mb-1">Category</div>
          <select
            data-testid="price-watch-category"
            value={form.category}
            onChange={e => setForm({ ...form, category: e.target.value })}
            className="w-full border border-border bg-white rounded-lg px-3 py-2.5 text-sm"
          >
            {CATEGORIES.map(c => <option key={c || 'all'} value={c}>{c ? c[0].toUpperCase() + c.slice(1) : 'Any category'}</option>)}
          </select>
        </div>
        <div className="md:col-span-2">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink/60 mb-1">Unit (optional)</div>
          <input
            data-testid="price-watch-unit"
            value={form.unit}
            onChange={e => setForm({ ...form, unit: e.target.value })}
            placeholder="kg / pack / pc"
            className="w-full border border-border bg-white rounded-lg px-3 py-2.5 text-sm"
          />
        </div>
        <div className="md:col-span-2">
          <button
            data-testid="price-watch-lookup-btn"
            onClick={() => lookup()}
            disabled={loading}
            className="btn-primary w-full"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Search className="w-4 h-4"/>}
            {loading ? 'Looking up…' : 'Look up'}
          </button>
        </div>
      </div>

      {result && (
        <div data-testid="price-watch-result" className="card-soft p-5 mt-5">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#0D47A1]">Latest price</div>
              <div className="font-heading font-bold text-xl text-ink mt-0.5">{result.product_name}</div>
              {result.notes && <div className="text-xs text-ink/60 mt-0.5">{result.notes}</div>}
            </div>
            <span className="pill !text-[10px] !bg-[#E3F2FD] !text-[#0D47A1]">
              {result.source === 'cache' ? '6-hr cache' : result.source === 'tavily+llm' ? 'Web · live' : result.source === 'llm-only' ? 'AI estimate' : 'Unavailable'}
            </span>
          </div>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-xl bg-[#FFF3E0] border border-[#FFCC80] p-4">
              <div className="text-[11px] uppercase tracking-wider font-bold text-[#9A4500] inline-flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5"/>Avg market price</div>
              <div data-testid="price-watch-avg" className="font-heading font-black text-3xl text-ink mt-1 tabular-nums">
                {result.avg_price_inr != null ? `₹${result.avg_price_inr}` : '—'}
              </div>
            </div>
            <div className="rounded-xl bg-[#E8F5E9] border border-[#A5D6A7] p-4">
              <div className="text-[11px] uppercase tracking-wider font-bold text-[#1B5E20] inline-flex items-center gap-1.5"><TrendingDown className="w-3.5 h-3.5"/>Lowest online</div>
              <div data-testid="price-watch-lowest" className="font-heading font-black text-3xl text-[#1B5E20] mt-1 tabular-nums">
                {result.lowest_price_inr != null ? `₹${result.lowest_price_inr}` : '—'}
              </div>
              {result.source_url && (
                <a data-testid="price-watch-source-link" href={result.source_url} target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs text-[#1B5E20] hover:underline font-semibold">
                  View on {result.source_name || 'source'} ↗
                </a>
              )}
              {!result.source_url && result.source_name && (
                <div className="mt-2 text-xs text-ink/60">Source: <span className="font-semibold">{result.source_name}</span></div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="card-soft p-5 mt-5">
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-ink/60"/>
          <div className="font-heading font-bold text-base">Recent platform lookups</div>
          <button onClick={loadRecent} className="ml-auto inline-flex items-center gap-1 text-xs text-ink/60 hover:text-[#0D47A1]"><RefreshCw className="w-3 h-3"/>Refresh</button>
        </div>
        {recent.length === 0 ? (
          <div className="text-sm text-ink/60 mt-2">No lookups yet. Search a product above to populate this history.</div>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-ink/50 border-b border-border">
                  <th className="py-2 pr-3">Product</th>
                  <th className="py-2 pr-3">Category</th>
                  <th className="py-2 pr-3 text-right">Avg ₹</th>
                  <th className="py-2 pr-3 text-right">Lowest ₹</th>
                  <th className="py-2 pr-3">Source</th>
                  <th className="py-2 pr-3">Looked up</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((r) => {
                  const key = `${r.product_name}::${r.cached_at}`;
                  return (
                  <tr key={key} className="border-b border-border/40 hover:bg-[#FFF8E1]/40 cursor-pointer"
                      data-testid={`price-watch-recent-${key}`}
                      onClick={() => { setForm({ product_name: r.product_name, category: r.category || '', unit: r.unit || '' }); lookup({ product_name: r.product_name, category: r.category || '', unit: r.unit || '' }); }}>
                    <td className="py-2 pr-3 font-semibold">{r.product_name}</td>
                    <td className="py-2 pr-3 text-ink/70">{r.category || '—'}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{r.data?.avg_price_inr ?? '—'}</td>
                    <td className="py-2 pr-3 text-right tabular-nums text-[#1B5E20] font-semibold">{r.data?.lowest_price_inr ?? '—'}</td>
                    <td className="py-2 pr-3 text-[11px]">
                      <span className={`pill !text-[9px] ${r.source === 'tavily+llm' ? '!bg-[#E8F5E9] !text-[#1B5E20]' : '!bg-[#FFF3E0] !text-[#9A4500]'}`}>
                        {r.source === 'tavily+llm' ? 'web' : 'estimate'}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-[11px] text-ink/60">{r.cached_at ? new Date(r.cached_at).toLocaleString() : '—'}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
